import React, { useState, useContext, useEffect, useCallback } from 'react';
import { post } from '../api/Api';
import Loader from './Loader';
import { useNavigate, useLocation } from 'react-router-dom';
import * as constants from '../constants/constants';
import { AuthContext } from '../contexts/AuthContext';
import Swal from 'sweetalert2';

function LoginFloatingForm({ isFormVisible, setIsFormVisible, scrollToCrearCuenta }) {

    const { setIsLoggedIn, setUserData } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Función para manejar el clic en la burbuja
    const toggleFormVisibility = () => {
        setIsFormVisible(!isFormVisible);
    };

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    })

    const handleChange = (e) => {
        setError('');
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmitForm = (e) => {
        e.preventDefault();

        // Validación de correo electrónico
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setError('El formato del correo electrónico no es válido.');
            return;
        }

        iniciarSesion();
        setError(''); // Reinicia el mensaje de error
    };


    const iniciarSesion = async () => {
        setIsLoading(true);

        try {
            const formDataEnviar = new FormData();
            formDataEnviar.append('email', formData.email);
            formDataEnviar.append('password', formData.password);

            const response = await post('iniciar_sesion', formDataEnviar);

            if (response.data.success) {
                setUserData(response.data.data);
                setIsLoggedIn(true);
                navigate(constants.CURSO);
            } else {
                setError(response.data.message);
            }
        } catch (error) {
            if (error) {
                setError(error?.response?.data?.message || error.message);
            }
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };


    const recuperarCuenta = () => {
        Swal.fire({
            title: "Recuperar Cuenta",
            text: "Ingresa tu correo electrónico para recibir un enlace de recuperación.",
            icon: "info",
            input: "email",
            inputPlaceholder: "tuemail@ejemplo.com",
            showCancelButton: true,
            confirmButtonText: "Confirmar",
            cancelButtonText: "Cancelar",
            showCloseButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            customClass: {
                cancelButton: 'auteco_button tertiary',
                confirmButton: 'auteco_button primary'
            },

            // Aquí se añade la validación de entrada
            inputValidator: (email) => {
                if (!email) {
                    return '¡Por favor, ingresa tu correo electrónico!';
                }
                // Puedes agregar más validaciones aquí si lo deseas
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                    return '¡Dirección de correo electrónico inválida!';
                }
            },

            preConfirm: async (email) => {
                try {
                    const formDataEnviar = new FormData();
                    formDataEnviar.append('email', email); // Usar el valor ingresado

                    const response = await post('recuperar_cuenta', formDataEnviar);

                    return response;

                } catch (error) {
                    console.error(error);
                    Swal.showValidationMessage(error?.response?.data?.message || error.message);
                }
            },

        }).then((result) => {
            if (result.isConfirmed) {
                if (result.value.data.success) {
                    Swal.fire({
                        title: "Éxito",
                        text: result.value.data.message,
                        icon: "success",
                        confirmButtonText: "Ok",
                        showCloseButton: true,
                        customClass: {
                            confirmButton: 'auteco_button primary'
                        },
                    });
                } else {
                    Swal.fire({
                        title: "Error",
                        text: result.value.data.message,
                        icon: "error",
                        confirmButtonText: "Ok",
                        showCloseButton: true,
                        customClass: {
                            confirmButton: 'auteco_button primary'
                        },
                    });
                }
            }
        });
    };


    const restablecerContrasena = useCallback((token, userId) => {
        Swal.fire({
            title: "Restablecer Contraseña",
            text: "Ingresa tu nueva contraseña.",
            icon: "info",
            input: "password",
            inputPlaceholder: "Nueva contraseña",
            showCancelButton: true,
            confirmButtonText: "Confirmar",
            cancelButtonText: "Cancelar",
            showCloseButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            customClass: {
                cancelButton: 'auteco_button tertiary',
                confirmButton: 'auteco_button primary'
            },

            inputValidator: (value) => {
                if (!value) {
                    return '¡Debes ingresar una contraseña!';
                }
            },
            preConfirm: async (newPassword) => {
                try {
                    const formDataEnviar = new FormData();
                    formDataEnviar.append('nueva_password', newPassword);
                    formDataEnviar.append('token', token);
                    formDataEnviar.append('user_id', userId);

                    const response = await post('restablecer_contrasena', formDataEnviar);

                    return response;
                } catch (error) {
                    console.error(error);
                    Swal.showValidationMessage(error?.response?.data?.message || error.message);
                }
            },

        }).then((result) => {
            if (result.isConfirmed) {
                if (result.value.data.success) {
                    Swal.fire({
                        title: "Éxito",
                        text: result.value.data.message,
                        icon: "success",
                        confirmButtonText: "Ok",
                        showCloseButton: true,
                        customClass: {
                            confirmButton: 'auteco_button primary'
                        },
                    }).then(() => {
                        navigate(constants.HOME);
                    });
                } else {
                    Swal.fire({
                        title: "Error",
                        text: result.value.data.message,
                        icon: "error",
                        confirmButtonText: "Ok",
                        showCloseButton: true,
                        customClass: {
                            confirmButton: 'auteco_button primary'
                        },
                    });
                }
            }
        });
    }, [navigate]);


    // Efecto para verificar si hay que restablecer contraseña
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const action = queryParams.get('action');
        const token = queryParams.get('token');
        const userId = queryParams.get('user_id');

        if (action === 'reset_password' && token && userId) {
            restablecerContrasena(token, userId);
        }
    }, [location.search, restablecerContrasena]);


    return (
        <>
            <div className='auteco_burbuja-flotante' onClick={toggleFormVisibility}>
                <span className="material-symbols-outlined">passkey</span>
            </div>

            {isFormVisible && (
                <div className='auteco_formulario-flotante'>
                    <form className='auteco_form' onSubmit={handleSubmitForm}>
                        <div className='titulo'><strong>Inicia sesión</strong> o crea tu cuenta</div>
                        <div className='auteco_input'>
                            <label> Correo electrónico:</label>
                            <input
                                name='email'
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className='auteco_input' style={{ position: "relative" }}>
                            <label>Contraseña</label>
                            <input
                                name='password'
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={handleChange}
                                required
                            />
                            <span
                                className="material-icons"
                                style={{
                                position: "absolute",
                                right: 10,
                                top: "50%",
                                cursor: "pointer"
                                }}
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? "visibility_off" : "visibility"}
                            </span>
                        </div>

                        <div className='olvidar-contraseña'>
                            <div onClick={recuperarCuenta}>¿Olvidaste tu contraseña?</div>
                        </div>

                        {isLoading ? (
                            <Loader />
                        ) : (
                            <div className='botones'>
                                <button className='auteco_button secondary' type="button" onClick={scrollToCrearCuenta}>
                                    Crear una cuenta
                                </button>
                                <button className='auteco_button primary' type="submit">Iniciar sesión</button>
                            </div>
                        )}

                        {error && <div className="auteco_error-message">{error}</div>}
                    </form>
                </div>
            )}
        </>
    )
}

export default LoginFloatingForm